import { useAuthQuery } from '@/hooks/useAuthQuery';
import { supabase } from '@/integrations/supabase/client';
import { PROJECT_STATUS_ORDER, type ProjectKpiStatus } from '@/constants/projectStatusLabels';
import type { LifecycleStageBuild, LifecycleStageTender } from '@/constants/lifecycleStages';
import type { ProjectFiltersData } from '@/hooks/useOptimizedProjectsQuery';


export type BuildStageKey = LifecycleStageBuild | 'unset';
export type TenderStageKey = LifecycleStageTender | 'unset';

export interface BuildStageStat {
  count: number;
  total_gfa: number;
}

export interface ProjectStatusStat {
  count: number;
  total_gfa: number;
  build_stages?: Record<BuildStageKey, BuildStageStat>;
  tender_stages?: Record<TenderStageKey, BuildStageStat>;
}

export type ProjectStatusStats = Record<ProjectKpiStatus, ProjectStatusStat>;

const emptyBuildStages = (): Record<BuildStageKey, BuildStageStat> => ({
  unset: { count: 0, total_gfa: 0 },
  m1_open: { count: 0, total_gfa: 0 },
  m2_structural: { count: 0, total_gfa: 0 },
  m3_mep: { count: 0, total_gfa: 0 },
  m4_finishing: { count: 0, total_gfa: 0 },
  m5_acceptance: { count: 0, total_gfa: 0 },
});

const emptyTenderStages = (): Record<TenderStageKey, BuildStageStat> => ({
  unset: { count: 0, total_gfa: 0 },
  t1_orientation: { count: 0, total_gfa: 0 },
  t2_submitted: { count: 0, total_gfa: 0 },
  t3_negotiation: { count: 0, total_gfa: 0 },
});

const EMPTY_STATS: ProjectStatusStats = {
  intake: { count: 0, total_gfa: 0 },
  tender: { count: 0, total_gfa: 0, tender_stages: emptyTenderStages() },
  build: { count: 0, total_gfa: 0, build_stages: emptyBuildStages() },
};

export const useProjectStatusStats = (filters?: ProjectFiltersData) => {
  return useAuthQuery<ProjectStatusStats>({
    queryKey: [
      'projects',
      'status-stats',
      filters?.searchQuery ?? '',
      filters?.company ?? '',
      filters?.projectGroup ?? '',
      (filters?.projectType ?? []).slice().sort().join(','),
      filters?.projectMark ?? '',
    ],
    queryFn: async () => {
      let query = supabase
        .from('projects')
        .select('status, gross_floor_area_m2, lifecycle_stage_build, lifecycle_stage_tender')
        .in('status', PROJECT_STATUS_ORDER as readonly ('intake' | 'tender' | 'build')[]);

      if (filters?.company) {
        query = query.eq('company', filters.company);
      }
      if (filters?.projectGroup) {
        query = query.eq('project_group', filters.projectGroup);
      }
      if (filters?.projectType && filters.projectType.length > 0) {
        query = query.in('project_type', filters.projectType);
      }
      if (filters?.searchQuery && filters.searchQuery.trim()) {
        const q = filters.searchQuery.trim();
        query = query.or(`project_name.ilike.%${q}%,description.ilike.%${q}%`);
      }
      if (
        filters?.projectMark !== undefined &&
        filters?.projectMark !== '' &&
        filters?.projectMark !== null
      ) {
        query = query.eq('project_mark', filters.projectMark as any);
      }

      const { data, error } = await query;
      if (error) throw error;




      const acc: ProjectStatusStats = {
        intake: { count: 0, total_gfa: 0 },
        tender: { count: 0, total_gfa: 0, tender_stages: emptyTenderStages() },
        build: { count: 0, total_gfa: 0, build_stages: emptyBuildStages() },
      };

      for (const row of (data ?? []) as Array<{
        status: ProjectKpiStatus;
        gross_floor_area_m2: number | null;
        lifecycle_stage_build: LifecycleStageBuild | null;
        lifecycle_stage_tender: LifecycleStageTender | null;
      }>) {
        const s = row.status;
        if (!acc[s]) continue;
        acc[s].count += 1;
        const gfa = Number(row.gross_floor_area_m2);
        const safeGfa = Number.isFinite(gfa) && gfa > 0 ? gfa : 0;
        acc[s].total_gfa += safeGfa;
        if (s === 'build' && acc.build.build_stages) {
          const key: BuildStageKey = row.lifecycle_stage_build ?? 'unset';
          acc.build.build_stages[key].count += 1;
          acc.build.build_stages[key].total_gfa += safeGfa;
        }
        if (s === 'tender' && acc.tender.tender_stages) {
          const key: TenderStageKey = row.lifecycle_stage_tender ?? 'unset';
          acc.tender.tender_stages[key].count += 1;
          acc.tender.tender_stages[key].total_gfa += safeGfa;
        }
      }

      return acc;
    },
    requiresAuth: true,
    requiresSession: true,
    staleTime: 60_000,
    refetchOnMount: true,
    placeholderData: (prev) => prev ?? EMPTY_STATS,
  });
};
